import type { Card, PlayedCard, Suit } from './card';
import type { Player, PlayerId, PlayerPublicState, PlayerPosition } from './player';

// Supported player counts
export type PlayerCount = 2 | 3 | 4;

/** Server's team identifier (based on player positions: even=A, odd=B) */
export type ServerTeamId = 'A' | 'B';

export enum GamePhase {
  LOBBY = 'lobby',
  SELECTING_HAKEM = 'selecting_hakem', // First hand: deal cards until Ace determines Hakem
  READY_TO_DEAL = 'ready_to_deal', // Waiting for Hakem to click "Deal Cards"
  DEALING = 'dealing', // Cards being dealt in batches
  TRUMP_SELECTION = 'trump_selection',
  // 2-player specific phases
  HAKEM_DISCARDING = 'hakem_discarding', // Hakem discards 3 cards
  OPPONENT_DISCARDING = 'opponent_discarding', // Opponent discards 2 cards
  DRAWING_FROM_STOCK = 'drawing_from_stock', // Alternating stock draws
  // Common phases
  PLAYING = 'playing',
  TRICK_END = 'trick_end',
  HAND_END = 'hand_end',
  GAME_END = 'game_end',
}

export interface TrickState {
  cards: PlayedCard[];
  leadSuit: Suit | null;
  winnerId: PlayerId | null;
}

// Team-based scoring for 4-player mode
export interface TeamHandScore {
  teamA: number; // Tricks won this hand (0-13)
  teamB: number;
}

export interface TeamGameScore {
  teamA: number; // Total points (target: 7)
  teamB: number;
}

// Individual scoring for 2-player and 3-player modes
export interface IndividualHandScore {
  [playerId: string]: number; // Tricks won this hand
}

export interface IndividualGameScore {
  [playerId: string]: number; // Total points (target: 7)
}

// Union types for flexibility
export type HandScore = TeamHandScore | IndividualHandScore;
export type GameScore = TeamGameScore | IndividualGameScore;

// Type guards
export function isTeamHandScore(score: HandScore): score is TeamHandScore {
  return 'teamA' in score && 'teamB' in score;
}

export function isIndividualHandScore(score: HandScore): score is IndividualHandScore {
  return !isTeamHandScore(score);
}

export function isTeamGameScore(score: GameScore): score is TeamGameScore {
  return 'teamA' in score && 'teamB' in score;
}

export function isIndividualGameScore(score: GameScore): score is IndividualGameScore {
  return !isTeamGameScore(score);
}

export interface GameState {
  roomId: string;
  phase: GamePhase;
  playerCount: PlayerCount; // 2, 3, or 4 players

  // Players (indexed by position: 0=South, 1=West, 2=North, 3=East)
  // For 2-player: only positions 0 (South) and 2 (North) are used
  // For 3-player: positions 0, 1, 2 (South, West, North)
  players: (Player | null)[];

  // Roles
  hakemId: PlayerId | null; // Current Hakem (trump maker)
  currentPlayerId: PlayerId | null; // Whose turn
  dealerId: PlayerId | null;

  // Trump
  trumpSuit: Suit | null;

  // Current hand
  currentTrick: TrickState;
  handScore: HandScore;
  trickNumber: number; // 1-13

  // Game score
  gameScore: GameScore;
  handNumber: number;
  pointsToWin: number; // 3 (short) or 7 (standard)

  // Timing
  lastActionTime: number; // timestamp
  turnTimeoutMs: number;

  // Dealing state
  dealingRound: number; // Current dealing round (0 = not dealing)
  stockPileCount: number; // 2-player: cards remaining in stock
}

// Client receives sanitized state (no other players' cards)
export interface ClientGameState {
  roomId: string;
  phase: GamePhase;
  playerCount: PlayerCount;

  players: PlayerPublicState[];
  myHand: Card[];
  myPosition: PlayerPosition;
  myPlayerId: PlayerId;

  hakemId: PlayerId | null;
  currentPlayerId: PlayerId | null;
  dealerId: PlayerId | null;

  trumpSuit: Suit | null;

  currentTrick: TrickState;
  handScore: HandScore;
  trickNumber: number;

  gameScore: GameScore;
  handNumber: number;
  pointsToWin: number; // 3 (short) or 7 (standard)

  // Host (room creator)
  hostId: PlayerId | null;

  // Convenience flags
  isMyTurn: boolean;
  isSelectingTrump: boolean;
  canStartGame: boolean;

  // Dealing state
  dealingRound: number; // Current dealing round (0 = not dealing)
  stockPileCount: number; // 2-player: cards remaining in stock
}

// Team-based hand result (4-player)
export interface TeamHandResult {
  type: 'team';
  winningTeam: ServerTeamId;
  points: number; // 1, 2, or 3
  isKot: boolean;
  isHakemKoti: boolean;
  newHakemId: PlayerId;
  teamATricks: number;
  teamBTricks: number;
}

// Individual hand result (2-player, 3-player)
export interface IndividualHandResult {
  type: 'individual';
  winnerId: PlayerId;
  points: number;
  isKot: boolean;
  isHakemKoti: boolean;
  newHakemId: PlayerId;
  tricksByPlayer: { [playerId: string]: number }; // Keys are PlayerId values
}

export type HandResult = TeamHandResult | IndividualHandResult;

export function createEmptyTrick(): TrickState {
  return {
    cards: [],
    leadSuit: null,
    winnerId: null,
  };
}

export function createEmptyHandScore(playerCount: PlayerCount = 4): HandScore {
  if (playerCount === 4) {
    return { teamA: 0, teamB: 0 };
  }
  return {}; // Individual scoring - will be populated with player IDs
}

export function createEmptyGameScore(playerCount: PlayerCount = 4): GameScore {
  if (playerCount === 4) {
    return { teamA: 0, teamB: 0 };
  }
  return {}; // Individual scoring - will be populated with player IDs
}
